/**
 * TeacherDashboard — studio analytics and income overview for teachers.
 *
 * Data sources (all live / real-time):
 *  - useAuth()          → identity, avatar
 *  - useStreak()        → teaching / creation streaks
 *  - useDashboardData() → wallet API (income, weekly), challenges
 *  - useApp()           → real capsule catalogue with published status
 *
 * Fully theme-aware (system / light / dark) via useStudioChrome().
 * Animated charts: SVG + Reanimated (draw-on line, sweep donut).
 * Animated progress bars: Reanimated spring.
 */
import {Fragment, type ReactNode, useEffect, useMemo, useState} from 'react'
import {
  ActivityIndicator,
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import Animated, {
  Easing,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import Svg, {
  Circle,
  Defs,
  Line,
  LinearGradient as SvgLinearGradient,
  Path,
  Stop,
  Text as SvgText,
} from 'react-native-svg'
import {LinearGradient} from 'expo-linear-gradient'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {DASHBOARD_ACCENT} from '#/lib/dashboardPalette'
import {readableInkOn, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome} from '#/themes-studio/useChrome'
import {useApp} from '../contexts/AppContext'
import {useStreak} from '../contexts/StreakContext'
import {useAuth} from '../hooks/useAuth'
import {type Transaction, useDashboardData} from '../hooks/useDashboardData'
import {DinarSymbol} from './DinarSymbol'

const AnimatedPath = Animated.createAnimatedComponent(Path)
const _AnimatedCircle = Animated.createAnimatedComponent(Circle)

const {width: SCREEN_W} = Dimensions.get('window')
const PAD = 16
const CHART_W = SCREEN_W - PAD * 2 - 28
const WEEK_DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

const TOP_STUDENTS = [
  {
    name: 'Alex',
    handle: '@alex',
    sessions: 8,
    capsules: 12,
    color: DASHBOARD_ACCENT.positive,
    rank: 1,
  },
  {
    name: 'Sam',
    handle: '@sam',
    sessions: 6,
    capsules: 9,
    color: DASHBOARD_ACCENT.live,
    rank: 2,
  },
  {
    name: 'Marie',
    handle: '@marie',
    sessions: 5,
    capsules: 7,
    color: DASHBOARD_ACCENT.attention,
    rank: 3,
  },
  {
    name: 'Lucas',
    handle: '@lucas',
    sessions: 4,
    capsules: 11,
    color: DASHBOARD_ACCENT.primary,
    rank: 4,
  },
]

// ── Animated Donut Chart ──────────────────────────────────────────────────────
function DonutChart({
  data,
  totalLabel,
  totalValue,
}: {
  data: {label: string; value: number; color: string}[]
  totalLabel: string
  totalValue: ReactNode
}) {
  const colors = useStudioChrome()
  const total = data.reduce((s, d) => s + d.value, 0)

  // Animate sweep progress 0 → 1
  const progress = useSharedValue(0)
  const dataKey = data.map(d => d.value).join(',')
  useEffect(() => {
    progress.value = 0
    progress.value = withTiming(1, {
      duration: 900,
      easing: Easing.out(Easing.cubic),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey])

  if (total === 0) {
    return (
      <View
        style={{
          width: 150,
          height: 150,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Text style={{color: colors.textMuted, fontSize: 11}}>
          <Trans>No data</Trans>
        </Text>
      </View>
    )
  }

  const size = 150
  const r = 56
  const cx = size / 2
  const cy = size / 2
  const strokeW = 20

  // Build arc segments (pure computation, no hooks)
  type ArcSeg = {
    fullPath: string
    color: string
    startFrac: number
    endFrac: number
  }
  let cumAngle = -Math.PI / 2
  let cumFrac = 0
  const arcSegments: ArcSeg[] = data.map(d => {
    const frac = d.value / total
    const angle = frac * 2 * Math.PI * 0.99
    const x1 = cx + r * Math.cos(cumAngle)
    const y1 = cy + r * Math.sin(cumAngle)
    cumAngle += angle
    const x2 = cx + r * Math.cos(cumAngle)
    const y2 = cy + r * Math.sin(cumAngle)
    const largeArc = angle > Math.PI ? 1 : 0
    cumAngle += 0.02
    const startFrac = cumFrac
    cumFrac += frac * 0.99
    return {
      fullPath: `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`,
      color: d.color,
      startFrac,
      endFrac: cumFrac,
    }
  })

  // Total arc length (circumference × 0.99)
  const arcLen = 2 * Math.PI * r * 0.99

  return (
    <View style={{width: size, height: size}}>
      <Svg width={size} height={size}>
        {/* Background ring */}
        <Circle
          cx={cx}
          cy={cy}
          r={r}
          stroke={colors.border}
          strokeWidth={strokeW}
          fill="none"
        />

        {/* Each segment is its own component so useAnimatedProps is never in a loop */}
        {arcSegments.map((seg, i) => (
          <DonutSegment
            key={i}
            seg={seg}
            progress={progress}
            strokeW={strokeW}
            arcLen={arcLen}
          />
        ))}

        <SvgText
          x={cx}
          y={cy - 10}
          textAnchor="middle"
          fill={colors.textMuted}
          fontSize={10}
          fontWeight="600">
          {totalLabel}
        </SvgText>
      </Svg>
      <View
        style={{
          position: 'absolute',
          top: cy + 2,
          left: 0,
          right: 0,
          alignItems: 'center',
        }}
        pointerEvents="none">
        {totalValue}
      </View>
    </View>
  )
}

// ── Donut Segment — isolated so useAnimatedProps is never in a loop ────────────
function DonutSegment({
  seg,
  progress,
  strokeW,
  arcLen,
}: {
  seg: {fullPath: string; color: string; startFrac: number; endFrac: number}
  progress: ReturnType<typeof useSharedValue<number>>
  strokeW: number
  arcLen: number
}) {
  const segLen = (seg.endFrac - seg.startFrac) * arcLen
  const offsetFrac = seg.startFrac

  const animatedProps = useAnimatedProps(() => {
    const p = Math.max(0, Math.min(1, progress.value))
    const dashOffset = arcLen - offsetFrac * arcLen
    return {
      strokeDashoffset: dashOffset - segLen * p * p + segLen * (1 - p),
      strokeDasharray: `${segLen} ${arcLen - segLen}`,
    }
  })

  return (
    <AnimatedPath
      d={seg.fullPath}
      stroke={seg.color}
      strokeWidth={strokeW}
      fill="none"
      strokeLinecap="butt"
      animatedProps={animatedProps}
    />
  )
}

// ── Animated Line Chart ───────────────────────────────────────────────────────
function LineChart({
  values,
  days,
  color,
}: {
  values: number[]
  days: string[]
  color: string
}) {
  const colors = useStudioChrome()
  const h = 120
  const w = CHART_W
  const padX = 20
  const padY = 20
  const maxVal = Math.max(...values, 0.01)
  const xs = values.map(
    (_, i) => padX + (i / Math.max(values.length - 1, 1)) * (w - padX * 2),
  )
  const ys = values.map(v => padY + (1 - v / maxVal) * (h - padY * 2))
  const pathD = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'} ${x} ${ys[i]}`)
    .join(' ')
  const areaD = `${pathD} L ${xs[xs.length - 1]} ${h - padY + 4} L ${xs[0]} ${h - padY + 4} Z`

  // Approximate path length for strokeDashoffset animation
  let _pathLen = 0
  for (let i = 1; i < xs.length; i++) {
    _pathLen += Math.sqrt((xs[i] - xs[i - 1]) ** 2 + (ys[i] - ys[i - 1]) ** 2)
  }
  const pathLength = Math.ceil(_pathLen) + 10

  const drawProgress = useSharedValue(0)
  const dataKey = values.join(',')
  useEffect(() => {
    drawProgress.value = 0
    drawProgress.value = withTiming(1, {
      duration: 1100,
      easing: Easing.out(Easing.cubic),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey])

  const lineProps = useAnimatedProps(() => ({
    strokeDashoffset: pathLength * (1 - drawProgress.value),
  }))

  const areaProps = useAnimatedProps(() => ({
    opacity: drawProgress.value * 0.6,
  }))

  return (
    <Svg width={w} height={h}>
      <Defs>
        <SvgLinearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
          <Stop offset="0" stopColor={color} stopOpacity={0.35} />
          <Stop offset="1" stopColor={color} stopOpacity={0.0} />
        </SvgLinearGradient>
      </Defs>

      {/* Grid lines */}
      {[0, 0.5, 1].map((t, i) => (
        <Line
          key={i}
          x1={padX}
          y1={padY + t * (h - padY * 2)}
          x2={w - padX}
          y2={padY + t * (h - padY * 2)}
          stroke={colors.border}
          strokeWidth={1}
          strokeDasharray="4,4"
        />
      ))}

      {/* Area fill — fades in */}
      <AnimatedPath
        d={areaD}
        fill="url(#chartFill)"
        animatedProps={areaProps}
      />

      {/* Line — draws on from left */}
      <AnimatedPath
        d={pathD}
        stroke={color}
        strokeWidth={2.5}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={`${pathLength}`}
        animatedProps={lineProps}
      />

      {/* Dots and labels (static, always shown) */}
      {xs.map((x, i) => (
        <Fragment key={i}>
          <Circle
            cx={x}
            cy={ys[i]}
            r={4}
            fill={color}
            stroke={colors.surface}
            strokeWidth={2}
          />
          {values[i] > 0 && (
            <SvgText
              x={x}
              y={ys[i] - 9}
              textAnchor="middle"
              fill={color}
              fontSize={9}
              fontWeight="700">
              {`${values[i].toFixed(2)} DT`}
            </SvgText>
          )}
          <SvgText
            x={x}
            y={h - 4}
            textAnchor="middle"
            fill={colors.textMuted}
            fontSize={10}>
            {days[i]}
          </SvgText>
        </Fragment>
      ))}
    </Svg>
  )
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({
  emoji,
  value,
  label,
  color,
  loading,
}: {
  emoji: string
  value: string | number
  label: string
  color: string
  loading?: boolean
}) {
  const colors = useStudioChrome()
  // The hue tints the card; the figure printed on it needs the readable step.
  const valueInk = readableInkOn(color, colors)
  const scale = useSharedValue(0.85)
  const opacity = useSharedValue(0)

  useEffect(() => {
    scale.value = withSpring(1, {damping: 10, stiffness: 80})
    opacity.value = withTiming(1, {duration: 350})
  }, [value])

  const animStyle = useAnimatedStyle(() => ({
    transform: [{scale: scale.value}],
    opacity: opacity.value,
  }))

  return (
    <Animated.View style={[{flex: 1}, animStyle]}>
      <LinearGradient
        colors={[color + '20', color + '08']}
        style={[kpiStyles.card, {borderColor: color + '30'}]}>
        <View style={[kpiStyles.iconBg, {backgroundColor: color + '22'}]}>
          <Text style={{fontSize: 18}}>{emoji}</Text>
        </View>
        {loading ? (
          <ActivityIndicator
            size="small"
            color={color}
            style={{marginVertical: 6}}
          />
        ) : (
          <Text style={[kpiStyles.value, {color: valueInk}]}>{value}</Text>
        )}
        <Text style={[kpiStyles.label, {color: colors.textMuted}]}>
          {label}
        </Text>
      </LinearGradient>
    </Animated.View>
  )
}

const kpiStyles = StyleSheet.create({
  card: {
    flex: 1,
    borderRadius: 18,
    borderWidth: 1,
    padding: 14,
    alignItems: 'center',
    gap: 4,
  },
  iconBg: {
    width: 40,
    height: 40,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 2,
  },
  value: {fontSize: 22, fontWeight: '900'},
  label: {
    fontSize: 9,
    textAlign: 'center',
    fontWeight: '700',
    letterSpacing: 0.3,
  },
})

// ── Animated Progress Bar ─────────────────────────────────────────────────────
function ProgressBar({
  value,
  max,
  color,
  label,
  labelNode,
}: {
  value: number
  max: number
  color: string
  label: string
  labelNode?: React.ReactNode
}) {
  const colors = useStudioChrome()
  const ink = readableInkOn(color, colors)
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  const [trackW, setTrackW] = useState(0)
  const barW = useSharedValue(0)

  useEffect(() => {
    if (trackW > 0) {
      barW.value = withSpring(trackW * (pct / 100), {
        damping: 14,
        stiffness: 80,
      })
    }
  }, [pct, trackW])

  const barStyle = useAnimatedStyle(() => ({
    width: barW.value,
  }))

  return (
    <View style={{marginBottom: 14}}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 5,
        }}>
        {labelNode != null ? (
          labelNode
        ) : (
          <Text style={{fontSize: 11, color: colors.text, fontWeight: '600'}}>
            {label}
          </Text>
        )}
        <Text style={{fontSize: 12, fontWeight: '800', color: ink}}>
          {Math.round(pct)}%
        </Text>
      </View>
      <View
        style={{
          height: 7,
          backgroundColor: colors.border,
          borderRadius: 4,
          overflow: 'hidden',
        }}
        onLayout={e => setTrackW(e.nativeEvent.layout.width)}>
        <Animated.View
          style={[
            {height: '100%', borderRadius: 4, overflow: 'hidden'},
            barStyle,
          ]}>
          <LinearGradient
            colors={[color, color + '99']}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 0}}
            style={{height: '100%', width: '100%'}}
          />
        </Animated.View>
      </View>
    </View>
  )
}

// ── Suggestion Card ───────────────────────────────────────────────────────────
function SuggestionCard({
  icon,
  title,
  desc,
  action,
  urgency,
}: {
  icon: string
  title: string
  desc: string
  action: string
  urgency: 'high' | 'medium' | 'low'
}) {
  const colors = useStudioChrome()
  const urgencyColor =
    urgency === 'high'
      ? colors.danger
      : urgency === 'medium'
        ? DASHBOARD_ACCENT.attention
        : colors.success
  return (
    <LinearGradient
      colors={[urgencyColor + '12', urgencyColor + '04']}
      style={[suggStyles.card, {borderColor: urgencyColor + '30'}]}>
      <View style={suggStyles.top}>
        <View
          style={[suggStyles.iconBg, {backgroundColor: urgencyColor + '20'}]}>
          <Text style={{fontSize: 18}}>{icon}</Text>
        </View>
        <View style={{flex: 1}}>
          <Text style={[suggStyles.title, {color: colors.text}]}>{title}</Text>
          <Text style={[suggStyles.desc, {color: colors.textMuted}]}>
            {desc}
          </Text>
        </View>
      </View>
      <TouchableOpacity
        accessibilityRole="button"
        style={[suggStyles.actionBtn, {backgroundColor: urgencyColor}]}
        activeOpacity={0.8}>
        <Text style={[suggStyles.actionText, {color: colors.onAccent}]}>
          → {action}
        </Text>
      </TouchableOpacity>
    </LinearGradient>
  )
}

const suggStyles = StyleSheet.create({
  card: {
    borderRadius: 18,
    borderWidth: 1,
    padding: 16,
    marginBottom: 10,
    gap: 12,
  },
  top: {flexDirection: 'row', alignItems: 'flex-start', gap: 12},
  iconBg: {
    width: 44,
    height: 44,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {fontSize: 14, fontWeight: '800', marginBottom: 4, lineHeight: 18},
  desc: {fontSize: 12, lineHeight: 17},
  actionBtn: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  actionText: {fontSize: 12, fontWeight: '800'},
})

// ── Section Header ────────────────────────────────────────────────────────────
function SectionHeader({
  title,
  badge,
  badgeColor,
  sub,
}: {
  title: string
  badge?: string
  badgeColor?: string
  sub?: string
}) {
  const colors = useStudioChrome()
  const bc = badgeColor ?? colors.accentFill
  const badgeInk = readableInkOn(bc, colors)
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        marginTop: 4,
      }}>
      <View
        style={{width: 3, height: 18, borderRadius: 2, backgroundColor: bc}}
      />
      <Text
        style={{
          fontSize: 14,
          fontWeight: '900',
          color: colors.text,
          flex: 1,
          letterSpacing: 0.2,
        }}>
        {title}
      </Text>
      {badge && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            paddingHorizontal: 10,
            paddingVertical: 4,
            borderRadius: 10,
            backgroundColor: bc + '18',
          }}>
          {sub && (
            <View
              style={{
                width: 5,
                height: 5,
                borderRadius: 3,
                backgroundColor: bc,
              }}
            />
          )}
          <Text style={{fontSize: 10, fontWeight: '700', color: badgeInk}}>
            {badge}
          </Text>
        </View>
      )}
    </View>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function TeacherDashboard() {
  const {t: l} = useLingui()
  const {user} = useAuth()
  const {streaks} = useStreak()
  const {capsules} = useApp()
  const {wallet, weeklyAmounts, sessionsHosted, loading, error, refetch} =
    useDashboardData(user?.id ?? '', 'teacher')

  const colors = useStudioChrome()
  const ss = useMemo(() => makeStyles(colors), [colors])
  const [activeTab, setActiveTab] = useState<'all' | 'classic' | 'live'>('all')

  const displayName = user?.displayName ?? 'Teacher'
  const avatarColor = user?.avatarColor ?? colors.success
  const initials = displayName
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const teachingStreak = streaks?.teaching
  const creationStreak = streaks?.creation

  const realBalance = wallet?.balance ?? 0
  const creditTxs: Transaction[] = (wallet?.transactions ?? []).filter(
    t => t.type === 'credit',
  )
  const weekTotal = weeklyAmounts.reduce((a, b) => a + b, 0)

  const liveRevenue = creditTxs
    .filter(
      t =>
        t.description.toLowerCase().includes('revenu') ||
        t.description.toLowerCase().includes('live'),
    )
    .reduce((s, t) => s + t.amount, 0)
  const capsuleRevenue = creditTxs
    .filter(
      t =>
        !t.description.toLowerCase().includes('live') &&
        !t.description.toLowerCase().includes('revenu') &&
        !t.description.toLowerCase().includes('challenge'),
    )
    .reduce((s, t) => s + t.amount, 0)
  const challengeRevenue = creditTxs
    .filter(
      t =>
        t.description.toLowerCase().includes('challenge') ||
        t.description.toLowerCase().includes('défi'),
    )
    .reduce((s, t) => s + t.amount, 0)

  const totalCredit = creditTxs.reduce((s, t) => s + t.amount, 0)
  const donutData =
    totalCredit > 0
      ? [
          {
            label: 'Live',
            value: Math.max(liveRevenue, 0),
            color: DASHBOARD_ACCENT.live,
          },
          {
            label: 'Capsules',
            value: Math.max(capsuleRevenue, 0),
            color: colors.accentFill,
          },
          {
            label: 'Challenges',
            value: Math.max(challengeRevenue, 0),
            color: DASHBOARD_ACCENT.engagement,
          },
        ]
      : [
          {label: 'Live', value: 3.45, color: DASHBOARD_ACCENT.live},
          {label: 'Capsules', value: 1.2, color: colors.accentFill},
          {
            label: 'Challenges',
            value: 0.48,
            color: DASHBOARD_ACCENT.engagement,
          },
        ]

  const CAPSULE_EMOJIS: Record<string, string> = {
    python: '🐍',
    web: '🌐',
    data: '📊',
    machine: '🤖',
    javascript: '⚡',
    math: '📐',
    default: '📖',
  }
  const getEmoji = (title: string) => {
    const t = title.toLowerCase()
    for (const [k, v] of Object.entries(CAPSULE_EMOJIS)) {
      if (t.includes(k)) return v
    }
    return CAPSULE_EMOJIS.default
  }

  const capsuleIncomeMap: Record<string, {sessions: number; income: number}> =
    {}
  for (const tx of creditTxs) {
    const m = tx.description.match(/[«"](.+?)[»"]/)
    const key = (m ? m[1].trim() : tx.description.slice(0, 40)).toLowerCase()
    if (!capsuleIncomeMap[key]) capsuleIncomeMap[key] = {sessions: 0, income: 0}
    capsuleIncomeMap[key].sessions += 1
    capsuleIncomeMap[key].income += tx.amount
  }

  const catalogueCapsules = useMemo(() => {
    const baseData = capsules.map(c => {
      const titleLow = c.title.toLowerCase()
      let match: {sessions: number; income: number} | undefined
      for (const [key, val] of Object.entries(capsuleIncomeMap)) {
        if (
          titleLow.includes(key) ||
          key.includes(titleLow.split(' ')[0]?.toLowerCase() ?? '')
        ) {
          match = val
          break
        }
      }
      return {
        id: c.id,
        title: c.title,
        emoji: getEmoji(c.title),
        type:
          c.themeId === 'html-bridge' || c.slides.some(s => s.type === 'video')
            ? ('live' as const)
            : ('classic' as const),
        published: c.status === 'published',
        sessions: match?.sessions ?? 0,
        income: match?.income ?? 0,
      }
    })
    if (baseData.length === 0) {
      return [
        {
          id: 'p1',
          title: 'Python Basics',
          emoji: '🐍',
          type: 'classic' as const,
          published: true,
          sessions: 0,
          income: 0,
        },
        {
          id: 'p2',
          title: 'Web Fundamentals',
          emoji: '🌐',
          type: 'classic' as const,
          published: true,
          sessions: 0,
          income: 0,
        },
        {
          id: 'p3',
          title: 'Data Science',
          emoji: '📊',
          type: 'live' as const,
          published: true,
          sessions: 0,
          income: 0,
        },
        {
          id: 'p4',
          title: 'Machine Learning',
          emoji: '🤖',
          type: 'live' as const,
          published: false,
          sessions: 0,
          income: 0,
        },
        {
          id: 'p5',
          title: 'JavaScript Deep',
          emoji: '⚡',
          type: 'classic' as const,
          published: true,
          sessions: 0,
          income: 0,
        },
      ]
    }
    return baseData
  }, [capsules, capsuleIncomeMap])

  const filteredCapsules =
    activeTab === 'all'
      ? catalogueCapsules
      : catalogueCapsules.filter(c => c.type === activeTab)
  const totalSessions = catalogueCapsules.reduce((s, c) => s + c.sessions, 0)
  const publishedCount = capsules.filter(c => c.status === 'published').length

  const liveShare =
    totalCredit > 0 ? Math.round((liveRevenue / totalCredit) * 100) : 0
  const activeDays = weeklyAmounts.filter(v => v > 0).length
  const weekActivity = Math.round((activeDays / 7) * 100)
  const rps = sessionsHosted > 0 ? totalCredit / sessionsHosted : 0
  const revenuePerSession = Math.min(Math.round((rps / 5) * 100), 100)
  const publishedShare =
    capsules.length > 0
      ? Math.round((publishedCount / capsules.length) * 100)
      : 0
  const insights = {liveShare, weekActivity, revenuePerSession, publishedShare}

  return (
    <ScrollView
      style={ss.container}
      contentContainerStyle={ss.content}
      showsVerticalScrollIndicator={false}>
      {/* ── Studio Hero Header ── */}
      <LinearGradient
        colors={[avatarColor + '22', avatarColor + '08', 'transparent']}
        style={ss.heroCard}>
        <View style={ss.heroTop}>
          <View style={[ss.avatarRing, {borderColor: avatarColor + '60'}]}>
            <View style={[ss.avatar, {backgroundColor: avatarColor + '28'}]}>
              <Text style={[ss.avatarText, {color: avatarColor}]}>
                {initials}
              </Text>
            </View>
          </View>
          <View style={ss.heroInfo}>
            <Text style={ss.greeting}>
              Studio · {displayName.split(' ')[0]}
            </Text>
            <Text style={[ss.handle, {color: colors.textMuted}]}>
              {user?.handle}
            </Text>
          </View>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={refetch}
            style={[
              ss.refreshBtn,
              {borderColor: colors.border, backgroundColor: colors.surface},
            ]}
            disabled={loading}>
            {loading ? (
              <ActivityIndicator size="small" color={colors.success} />
            ) : (
              <Feather name="refresh-cw" size={15} color={colors.textMuted} />
            )}
          </TouchableOpacity>
        </View>

        {/* Balance hero */}
        <View style={ss.balanceSection}>
          <View>
            <Text
              style={[
                ss.balanceLabel,
                {color: readableInkOn(colors.success, colors)},
              ]}>
              <Trans>Available balance</Trans>
            </Text>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 5}}>
              <DinarSymbol size={22} color={colors.text} />
              <Text style={ss.balanceAmount}>
                {loading ? '—' : balance_fmt(realBalance)}
              </Text>
            </View>
            {weekTotal > 0 ? (
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
                <Text style={[ss.balanceSub, {color: colors.textMuted}]}>
                  +
                </Text>
                <DinarSymbol size={10} color={colors.textMuted} />
                <Text style={[ss.balanceSub, {color: colors.textMuted}]}>
                  {weekTotal.toFixed(2)} cette semaine
                </Text>
              </View>
            ) : (
              <Text style={[ss.balanceSub, {color: colors.textMuted}]}>
                <Trans>No revenue this week</Trans>
              </Text>
            )}
          </View>
          <LinearGradient
            colors={[colors.success + '30', colors.success + '12']}
            style={ss.balanceIconBg}>
            <Feather name="trending-up" size={24} color={colors.success} />
          </LinearGradient>
        </View>
      </LinearGradient>

      {/* Error banner */}
      {error && (
        <View
          style={[
            ss.errorBanner,
            {
              backgroundColor: colors.danger + '15',
              borderColor: colors.danger + '40',
            },
          ]}>
          <Feather name="wifi-off" size={13} color={colors.danger} />
          <Text
            style={[
              ss.errorText,
              {color: readableInkOn(colors.danger, colors)},
            ]}>
            <Trans>API offline · demo data</Trans>
          </Text>
        </View>
      )}

      {/* ── KPI Cards ── */}
      <View style={ss.kpiRow}>
        <KpiCard
          emoji="🎓"
          value={teachingStreak?.current ?? 0}
          label={l`Teaching streak`}
          color={DASHBOARD_ACCENT.attention}
        />
        <KpiCard
          emoji="✍️"
          value={creationStreak?.current ?? 0}
          label={l`Creation streak`}
          color={DASHBOARD_ACCENT.live}
        />
        <KpiCard
          emoji="📺"
          value={loading ? '…' : sessionsHosted}
          label={l`Lives`}
          color={colors.accentFill}
        />
        <KpiCard
          emoji="📚"
          value={totalSessions}
          label={l`Sessions`}
          color={DASHBOARD_ACCENT.engagement}
        />
      </View>

      {/* ── Recent Income ── */}
      {creditTxs.length > 0 && (
        <>
          <SectionHeader
            title={l`Recent revenue`}
            badge={l`Real time`}
            badgeColor={colors.success}
            sub="dot"
          />
          <View style={[ss.card, {borderColor: colors.border}]}>
            {creditTxs.slice(0, 5).map((tx, idx) => (
              <View
                key={tx.id}
                style={[
                  ss.txRow,
                  idx < Math.min(creditTxs.length, 5) - 1 && {
                    borderBottomWidth: 1,
                    borderBottomColor: colors.border,
                  },
                ]}>
                <LinearGradient
                  colors={[colors.success + '25', colors.success + '0A']}
                  style={ss.txIconBg}>
                  <Feather
                    name="arrow-down-left"
                    size={15}
                    color={colors.success}
                  />
                </LinearGradient>
                <View style={ss.txInfo}>
                  <Text
                    style={[ss.txDesc, {color: colors.text}]}
                    numberOfLines={1}>
                    {tx.description}
                  </Text>
                  <Text style={[ss.txDate, {color: colors.textMuted}]}>
                    {new Date(tx.timestamp).toLocaleDateString('fr-FR')}
                  </Text>
                </View>
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
                  <Text
                    style={[
                      ss.txAmount,
                      {color: readableInkOn(colors.success, colors)},
                    ]}>
                    +
                  </Text>
                  <DinarSymbol size={12} color={colors.success} />
                  <Text
                    style={[
                      ss.txAmount,
                      {color: readableInkOn(colors.success, colors)},
                    ]}>
                    {tx.amount.toFixed(2)}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </>
      )}

      {/* ── Revenue Donut ── */}
      <SectionHeader
        title={l`Revenue breakdown`}
        badgeColor={DASHBOARD_ACCENT.live}
      />
      <View style={[ss.card, {borderColor: colors.border}]}>
        <View style={ss.donutRow}>
          <DonutChart
            data={donutData}
            totalLabel="Solde"
            totalValue={
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
                <DinarSymbol size={13} color={colors.text} />
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: '900',
                    color: colors.text,
                  }}>
                  {realBalance.toFixed(2)}
                </Text>
              </View>
            }
          />
          <View style={ss.donutLegend}>
            {donutData.map((d, i) => (
              <View key={i} style={ss.legendItem}>
                <View style={[ss.legendDot, {backgroundColor: d.color}]} />
                <View>
                  <Text style={[ss.legendLabel, {color: colors.text}]}>
                    {d.label}
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 2,
                    }}>
                    <DinarSymbol size={11} color={d.color} />
                    <Text
                      style={[
                        ss.legendValue,
                        {color: readableInkOn(d.color, colors)},
                      ]}>
                      {d.value.toFixed(2)}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* ── Weekly Income Chart ── */}
      <SectionHeader
        title={l`Revenue last 7 days`}
        badgeColor={colors.success}
      />
      <View style={[ss.card, {borderColor: colors.border}]}>
        <View style={ss.chartHeader}>
          <View>
            <Text style={[ss.chartLabel, {color: colors.textMuted}]}>
              <Trans>Total this week</Trans>
            </Text>
            {loading ? (
              <Text
                style={[
                  ss.chartValue,
                  {color: readableInkOn(colors.success, colors)},
                ]}>
                …
              </Text>
            ) : (
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 3}}>
                <DinarSymbol size={14} color={colors.success} />
                <Text
                  style={[
                    ss.chartValue,
                    {color: readableInkOn(colors.success, colors)},
                  ]}>
                  {weekTotal.toFixed(2)}
                </Text>
              </View>
            )}
          </View>
          {weekTotal > 0 && (
            <View
              style={[ss.liveChip, {backgroundColor: colors.success + '18'}]}>
              <View style={[ss.liveDot, {backgroundColor: colors.success}]} />
              <Text
                style={[
                  ss.liveChipText,
                  {color: readableInkOn(colors.success, colors)},
                ]}>
                <Trans>Live data</Trans>
              </Text>
            </View>
          )}
        </View>
        <LineChart
          values={weeklyAmounts.map(v => Math.round(v * 100) / 100)}
          days={WEEK_DAYS}
          color={colors.success}
        />
      </View>

      {/* ── Capsule Performance ── */}
      <SectionHeader
        title={l`Capsule performance`}
        badge={`${publishedCount || catalogueCapsules.length} ${(publishedCount || catalogueCapsules.length) !== 1 ? l`published` : l`published`}`}
        badgeColor={colors.accentFill}
      />

      {/* Tab switcher */}
      <View style={ss.tabRow}>
        {[
          {key: 'all' as const, label: l`All`, icon: '📋'},
          {key: 'classic' as const, label: l`Capsules`, icon: '📖'},
          {key: 'live' as const, label: l`Live`, icon: '📺'},
        ].map(t => (
          <TouchableOpacity
            accessibilityRole="button"
            key={t.key}
            style={[
              ss.tab,
              activeTab === t.key && {
                backgroundColor: colors.accentFill + '18',
                borderColor: colors.accentFill,
              },
            ]}
            onPress={() => setActiveTab(t.key)}
            activeOpacity={0.7}>
            <Text
              style={[
                ss.tabText,
                {
                  color:
                    activeTab === t.key ? colors.accentFill : colors.textMuted,
                },
              ]}>
              {t.icon} {t.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {filteredCapsules.length === 0 ? (
        <View
          style={[
            ss.emptyCard,
            {backgroundColor: colors.surface, borderColor: colors.border},
          ]}>
          <Feather name="book-open" size={22} color={colors.textMuted} />
          <Text style={[ss.emptyText, {color: colors.textMuted}]}>
            <Trans>No capsule in this category.</Trans>
          </Text>
        </View>
      ) : (
        filteredCapsules.map(cap => {
          const isLive = cap.type === 'live'
          const capColor = isLive ? DASHBOARD_ACCENT.live : colors.accentFill
          const sharePct =
            totalCredit > 0 && cap.income > 0
              ? Math.min((cap.income / totalCredit) * 100, 100)
              : 0
          return (
            <LinearGradient
              key={cap.id}
              colors={[capColor + '10', capColor + '04']}
              style={[ss.capsuleCard, {borderColor: capColor + '30'}]}>
              <View style={ss.capsuleTop}>
                <View
                  style={[
                    ss.capsuleEmojiBox,
                    {backgroundColor: capColor + '20'},
                  ]}>
                  <Text style={{fontSize: 20}}>{cap.emoji}</Text>
                </View>
                <View style={ss.capsuleInfo}>
                  <View style={ss.capsuleNameRow}>
                    <Text
                      style={[ss.capsuleName, {color: colors.text}]}
                      numberOfLines={1}>
                      {cap.title}
                    </Text>
                    <View
                      style={[
                        ss.typeBadge,
                        {backgroundColor: capColor + '20'},
                      ]}>
                      <Text
                        style={[
                          ss.typeText,
                          {color: readableInkOn(capColor, colors)},
                        ]}>
                        {isLive ? 'Live' : 'Classic'}
                      </Text>
                    </View>
                  </View>
                  <View style={ss.capsuleStats}>
                    <Feather name="users" size={10} color={colors.textMuted} />
                    <Text style={[ss.capsuleStat, {color: colors.textMuted}]}>
                      {cap.sessions > 0
                        ? `${cap.sessions} session${cap.sessions !== 1 ? 's' : ''}`
                        : '—'}
                    </Text>
                    <Text style={ss.capsuleDot}>·</Text>
                    <Feather
                      name="dollar-sign"
                      size={10}
                      color={colors.success}
                    />
                    {cap.income > 0 ? (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 1,
                        }}>
                        <DinarSymbol size={10} color={colors.success} />
                        <Text
                          style={[
                            ss.capsuleStat,
                            {
                              color: readableInkOn(colors.success, colors),
                              fontWeight: '700',
                            },
                          ]}>
                          {cap.income.toFixed(2)}
                        </Text>
                      </View>
                    ) : (
                      <Text
                        style={[
                          ss.capsuleStat,
                          {
                            color: readableInkOn(colors.success, colors),
                            fontWeight: '700',
                          },
                        ]}>
                        —
                      </Text>
                    )}
                  </View>
                </View>
                <View
                  style={[
                    ss.publishedBadge,
                    {
                      backgroundColor: cap.published
                        ? colors.success + '18'
                        : colors.danger + '15',
                    },
                  ]}>
                  <Feather
                    name={cap.published ? 'check-circle' : 'clock'}
                    size={11}
                    color={cap.published ? colors.success : colors.danger}
                  />
                </View>
              </View>

              {/* Income share bar — animated */}
              <ProgressBar
                value={sharePct}
                max={100}
                color={capColor}
                label=""
              />
              <Text
                style={{
                  fontSize: 10,
                  color: colors.textMuted,
                  marginTop: -10,
                  textAlign: 'right',
                }}>
                {sharePct > 0 ? `${Math.round(sharePct)}% du total` : '0%'}
              </Text>
            </LinearGradient>
          )
        })
      )}

      {/* ── Audience Insights ── */}
      <SectionHeader
        title={l`Audience Insights`}
        badgeColor={DASHBOARD_ACCENT.attention}
      />
      <View style={[ss.card, {borderColor: colors.border}]}>
        <ProgressBar
          value={insights.liveShare}
          max={100}
          color={colors.success}
          label={l`Live revenue share`}
        />
        <ProgressBar
          value={insights.weekActivity}
          max={100}
          color={colors.accentFill}
          label={l`Activity this week`}
        />
        <ProgressBar
          value={insights.revenuePerSession}
          max={100}
          color={DASHBOARD_ACCENT.attention}
          label=""
          labelNode={
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
              <Text
                style={{
                  fontSize: 11,
                  color: colors.text,
                  fontWeight: '600',
                }}>
                <Trans>Revenue / session (×5 </Trans>
              </Text>
              <DinarSymbol size={9} color={colors.text} />
              <Text
                style={{
                  fontSize: 11,
                  color: colors.text,
                  fontWeight: '600',
                }}>
                )
              </Text>
            </View>
          }
        />
        <ProgressBar
          value={insights.publishedShare}
          max={100}
          color={DASHBOARD_ACCENT.live}
          label={l`Published capsules`}
        />

        <View style={[ss.divider, {backgroundColor: colors.border}]} />

        <Text style={[ss.subTitle, {color: colors.textMuted}]}>
          <Trans>Top Students</Trans>
        </Text>
        {TOP_STUDENTS.map((s, idx) => (
          <View
            key={s.name}
            style={[
              ss.studentRow,
              idx < TOP_STUDENTS.length - 1 && {
                borderBottomWidth: 1,
                borderBottomColor: colors.border,
              },
            ]}>
            <View
              style={[
                ss.studentRank,
                {
                  backgroundColor:
                    s.rank === 1
                      ? DASHBOARD_ACCENT.attention + '22'
                      : s.rank === 2
                        ? DASHBOARD_ACCENT.live + '18'
                        : colors.fill,
                },
              ]}>
              <Text
                style={[
                  ss.studentRankText,
                  {
                    color:
                      s.rank === 1
                        ? readableInkOn(DASHBOARD_ACCENT.attention, colors)
                        : s.rank === 2
                          ? readableInkOn(DASHBOARD_ACCENT.live, colors)
                          : colors.textMuted,
                  },
                ]}>
                {s.rank === 1 ? '🥇' : s.rank === 2 ? '🥈' : `#${s.rank}`}
              </Text>
            </View>
            <View
              style={[
                ss.studentAvatar,
                {backgroundColor: s.color + '22', borderColor: s.color + '60'},
              ]}>
              <Text
                style={[
                  ss.studentAvatarText,
                  {color: readableInkOn(s.color, colors)},
                ]}>
                {s.name[0]}
              </Text>
            </View>
            <View style={{flex: 1}}>
              <Text style={[ss.studentName, {color: colors.text}]}>
                {s.name}
              </Text>
              <Text style={[ss.studentHandle, {color: colors.textMuted}]}>
                {s.handle}
              </Text>
            </View>
            <View style={{alignItems: 'flex-end', gap: 2}}>
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
                <Feather name="video" size={9} color={DASHBOARD_ACCENT.live} />
                <Text style={[ss.studentStat, {color: colors.textMuted}]}>
                  {s.sessions} live
                </Text>
              </View>
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
                <Feather name="book-open" size={9} color={colors.accentFill} />
                <Text style={[ss.studentStat, {color: colors.textMuted}]}>
                  {s.capsules} caps.
                </Text>
              </View>
            </View>
          </View>
        ))}
      </View>

      {/* ── Smart Suggestions ── */}
      <SectionHeader
        title={l`Recommended actions`}
        badgeColor={colors.danger}
      />
      <SuggestionCard
        icon="🔥"
        urgency="high"
        title={l`Boost your most popular capsule`}
        desc={l`The top capsule has strong potential. Add a final quiz to reach 90%+.`}
        action={l`Edit capsule`}
      />
      <SuggestionCard
        icon="📅"
        urgency="high"
        title={l`Schedule your next live session`}
        desc={l`Your students are waiting for the next session. Keep your streak!`}
        action={l`Create a session`}
      />
      <SuggestionCard
        icon="📢"
        urgency="medium"
        title={l`Share on Taderok`}
        desc={l`Reach 3× more audience by sharing your new capsule.`}
        action={l`Share now`}
      />
      <SuggestionCard
        icon="🎯"
        urgency="low"
        title={l`Create a challenge to boost engagement`}
        desc={l`Challenges generate 40% more engagement.`}
        action={l`Create a challenge`}
      />

      <View style={{height: 110}} />
    </ScrollView>
  )
}

function balance_fmt(n: number): string {
  return n.toFixed(2)
}

// ── Styles ─────────────────────────────────────────────────────────────────────
function makeStyles(colors: StudioChrome) {
  return StyleSheet.create({
    container: {flex: 1, backgroundColor: colors.frame},
    content: {padding: PAD, paddingTop: 8},

    heroCard: {borderRadius: 22, padding: 18, marginBottom: 18},
    heroTop: {flexDirection: 'row', alignItems: 'center', marginBottom: 18},
    avatarRing: {
      width: 60,
      height: 60,
      borderRadius: 30,
      borderWidth: 2,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    avatar: {
      width: 52,
      height: 52,
      borderRadius: 26,
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarText: {fontSize: 20, fontWeight: '900'},
    heroInfo: {flex: 1},
    greeting: {fontSize: 18, fontWeight: '800', color: colors.text},
    handle: {fontSize: 12, marginTop: 2},
    refreshBtn: {
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
    },

    balanceSection: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderTopWidth: 1,
      borderTopColor: colors.border + '50',
      paddingTop: 16,
    },
    balanceLabel: {
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      marginBottom: 4,
    },
    balanceAmount: {
      fontSize: 46,
      fontWeight: '900',
      color: colors.text,
      letterSpacing: -1.5,
    },

    balanceSub: {fontSize: 12, fontWeight: '500', marginTop: 2},
    balanceIconBg: {
      width: 56,
      height: 56,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
    },

    errorBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderRadius: 12,
      padding: 10,
      marginBottom: 16,
      borderWidth: 1,
    },
    errorText: {fontSize: 11, fontWeight: '500', flex: 1},

    kpiRow: {flexDirection: 'row', gap: 8, marginBottom: 22},

    card: {
      backgroundColor: colors.surface,
      borderRadius: 18,
      borderWidth: 1,
      padding: 16,
      marginBottom: 20,
    },
    divider: {height: 1, marginVertical: 14},
    subTitle: {
      fontSize: 10,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 1,
      marginBottom: 12,
      color: colors.textMuted,
    },

    txRow: {flexDirection: 'row', alignItems: 'center', paddingVertical: 12},
    txIconBg: {
      width: 40,
      height: 40,
      borderRadius: 13,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    txInfo: {flex: 1},
    txDesc: {fontSize: 13, fontWeight: '700'},
    txDate: {fontSize: 10, marginTop: 3},
    txAmount: {fontSize: 14, fontWeight: '900'},

    donutRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    donutLegend: {flex: 1, paddingLeft: 18, gap: 16},
    legendItem: {flexDirection: 'row', alignItems: 'center', gap: 12},
    legendDot: {width: 14, height: 14, borderRadius: 7},
    legendLabel: {fontSize: 12, fontWeight: '700'},
    legendValue: {fontSize: 17, fontWeight: '900'},

    chartHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 14,
    },
    chartLabel: {fontSize: 11, fontWeight: '600', marginBottom: 2},
    chartValue: {fontSize: 26, fontWeight: '900'},
    liveChip: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 12,
    },
    liveDot: {width: 6, height: 6, borderRadius: 3, marginRight: 6},
    liveChipText: {fontSize: 11, fontWeight: '700'},

    tabRow: {flexDirection: 'row', gap: 8, marginBottom: 12},
    tab: {
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 20,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    tabText: {fontSize: 12, fontWeight: '700'},

    capsuleCard: {
      borderRadius: 18,
      borderWidth: 1,
      padding: 16,
      marginBottom: 10,
    },
    capsuleTop: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
      gap: 12,
    },
    capsuleEmojiBox: {
      width: 46,
      height: 46,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    capsuleInfo: {flex: 1},
    capsuleNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 5,
    },
    capsuleName: {flex: 1, fontSize: 14, fontWeight: '800'},
    typeBadge: {paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8},
    typeText: {fontSize: 9, fontWeight: '800', letterSpacing: 0.3},
    capsuleStats: {flexDirection: 'row', alignItems: 'center', gap: 5},
    capsuleStat: {fontSize: 11},
    capsuleDot: {color: 'gray', fontSize: 11},
    publishedBadge: {
      width: 28,
      height: 28,
      borderRadius: 9,
      alignItems: 'center',
      justifyContent: 'center',
    },

    emptyCard: {
      borderRadius: 16,
      borderWidth: 1,
      padding: 28,
      alignItems: 'center',
      gap: 10,
      marginBottom: 20,
      flexDirection: 'row',
    },
    emptyText: {fontSize: 13, flex: 1, paddingLeft: 12},

    studentRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      gap: 10,
    },
    studentRank: {
      width: 32,
      height: 32,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    studentRankText: {fontSize: 14, fontWeight: '700'},
    studentAvatar: {
      width: 36,
      height: 36,
      borderRadius: 18,
      borderWidth: 1.5,
      alignItems: 'center',
      justifyContent: 'center',
    },
    studentAvatarText: {fontSize: 14, fontWeight: '800'},
    studentName: {fontSize: 13, fontWeight: '700'},
    studentHandle: {fontSize: 10},
    studentStat: {fontSize: 10},
  })
}
